/*
 * @Author: qian.tang
 * @Date: 2022-12-19 14:26:07
 * @LastEditors: Qian Tang qian@itrazotracetech.com
 * @LastEditTime: 2022-12-19 15:31:05
 * @FilePath: \itrazo-portal-dev\src\components\AdiTable\index.tsx
 * @Description: project table elements
 *
 * Copyright (c) 2022 by Qian Tang qian@itrazotracetech.com, All Rights Reserved.
 */
import React, { useState, MouseEvent, ChangeEvent, useEffect } from "react";
import { Table } from "antd";
import styled from "styled-components";
import { checkWindowSelectedText } from "utilities/Functions/GlobalHelperFunctions";
import { theme } from "statics/styles/StyledComponents";
import { FilterFilled } from "@ant-design/icons";
import { useResponsive } from "utilities/hooks";
import ActionDropdown from "./ActionDropdown";
import { NoActionDisplay } from "./NoActionDropdown";

type Props = {
  columns: any[];
  tableData: any[];
  rowKey?: any;
  noPagination?: boolean;
  onRowClick?: Function;
  onRowDoubleClick?: Function;
  onRowMouseEnter?: Function;
  onRowMouseLeave?: Function;
  fullWidth?: boolean;
  marginTop?: string;
  marginBottom?: string;
  className?: string;
  tableWrapperStyle?: object;
  sticky?: boolean;
  scroll?: any;
  rowSelection?: any;
  grid?: boolean;
  showHeader?: boolean;
  expandedRowRender?: any;
  expandable?: any;
  expandedRowKeys?: string[];
  disableRow?: (record: any, index: any) => boolean;
  loading?: boolean;
  extraPaginationPadding?: boolean;
};
/**
 * 
 * The "grid" prop is used to add borders to all the table cells
 * If only the table border is wanted, this can be added using the "tableWrapperStyle" prop
 */

const AdiTable = (props: Props) => {
  const {
    columns,
    tableData,
    rowKey,
    noPagination,
    onRowClick,
    onRowDoubleClick,
    onRowMouseEnter,
    onRowMouseLeave,
    fullWidth,
    className,
    marginTop,
    marginBottom,
    tableWrapperStyle,
    sticky,
    scroll,
    rowSelection,
    grid,
    showHeader,
    expandedRowRender,
    expandable,
    expandedRowKeys,
    disableRow,
    loading,
    extraPaginationPadding,
  } = props;

  const screen = useResponsive()

  let finalTableWrapperStyle: any = {
    width: fullWidth ? '100%' : '98%',
    paddingRight: fullWidth ? 0 : "5px",
    marginTop: marginTop || '2rem',
    marginBottom: marginBottom || '0px',
  }

  if (tableWrapperStyle) {
    finalTableWrapperStyle = {
      ...finalTableWrapperStyle,
      ...tableWrapperStyle
    }
  }

  // remove the "fixed" property from all columns when the screen size is xs
  if (screen.xs) {
    columns.forEach((column: any) => {
      column.fixed = undefined
    })
  }

  useEffect(() => {
    // update filer icon when filter is applied
    // to remove the hover text on the filter icon
    columns.forEach((column: any) => {
      if (column.filters) {
        column.filterIcon = (filtered: any) => (
          <span title=''><FilterFilled /></span>
        );
      }
    })
  }, [columns])



  return (
    <>
      <AntdTableWrapper grid={grid} style={{ ...finalTableWrapperStyle }} extraPaginationPadding={extraPaginationPadding}>
        <Table
          id='adi-table'
          loading={loading}
          className={`${className} tablecustomise`}
          columns={columns}
          showHeader={showHeader}
          dataSource={tableData}
          rowKey={rowKey}
          rowSelection={rowSelection}
          expandedRowRender={expandedRowRender}
          expandable={expandable}
          expandedRowKeys={expandedRowKeys}
          // scroll={scroll || { y: 'calc(100vh - 300px)', x: '900px' }}
          scroll={scroll || { x: '100%' }}
          // onChange={(pagination, filters, sorter, currentPageData) =>
          // //   filterD(currentPageData.currentDataSource)
          // }
          pagination={
            !noPagination && {
              // total: tableData.length,  // do not set a total here, does not work with filter
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ["15", "50", "100", "200", "500"],
              defaultPageSize: 15,
            }
          }
          rowClassName={(record, index) => {
            // swap light and dark as required
            const disable = disableRow ? disableRow(record, index) : false
            return index % 2 === 0 ? (`${onRowClick && !disable ? 'table-row-pointer' : ''} table-row-dark`) : (`${onRowClick && !disable ? 'table-row-pointer' : ''} table-row-light`)
          }}
          sticky={sticky}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                // ignore onRowClick if text is being selected
                if (checkWindowSelectedText()) return;
                onRowClick && onRowClick(record, rowIndex, event);
              }, // click row
              onDoubleClick: (event) => {
                // console.log(" row onDoubleClick");
                onRowDoubleClick && onRowDoubleClick(record, rowIndex, event);
              }, // double click row
              onContextMenu: (event) => {
                // console.log(" row onContextMenu");
                // onRowClick && onRowClick(record, rowIndex, event);
              }, // right button click row
              onMouseEnter: (event) => {
                // console.log(" row onMouseEnter");
                onRowMouseEnter && onRowMouseEnter(record, rowIndex, event);
              }, // mouse enter row
              onMouseLeave: (event) => {
                // console.log(" row onRowMouseLeave");
                onRowMouseLeave && onRowMouseLeave(record, rowIndex, event);
              }, // mouse leave row
            };
          }}
        />
      </AntdTableWrapper>
    </>
  );
};

AdiTable.ActionDropdown = ActionDropdown;
AdiTable.NoActionDisplay = NoActionDisplay;


export default AdiTable;


const AntdTableWrapper = styled.div<{ grid?: boolean; extraPaginationPadding?: boolean }>`
  width: 98%;
  .ant-checkbox-checked .ant-checkbox-inner:after {
    border-color: #fff !important;
  }
  // .ant-table-cell-fix-right .ant-table-cell-scrollbar {
  //   right: 0 !important;
  // }
  .ant-table-sticky-scroll-bar {
    background-color: #444444 !important;
  }
  .ant-table-sticky-holder {
    top: -20px !important;
    border-radius: var(--border-radius) !important;
  }
  ${({ grid }: { grid?: boolean }) => {
    if (grid) {
      return `
        /* Add the vertical borders to header  */
        .ant-table-thead > tr > .ant-table-cell {
          border: 1px solid var(--table-row-border-color) !important;;
        }
        /* Add the vertical borders to row */
        .ant-table-tbody > tr > td {
          border: 1px solid var(--table-row-border-color) !important;
        }
        `
    }
  }
  }



  .ant-table-pagination.ant-pagination {
    display: block !important;
    color: white;
    float: right;
    padding: ${(props) => (props.extraPaginationPadding ? `0 10px 0 0` : 0)};
  }

  .ant-pagination-item {
    border: none;
    background-color: transparent;

    &:hover {
      border-radius: var(--border-radius);
      border: 1px solid #f5f5f5;
    }
    a {
      position: relative;
      top: 3px;
      color: white;
    }
  }
  .ant-pagination-item-active {
    background: transparent !important;
    border: 1px solid #f5f5f5;
    border-radius: var(--border-radius);
    opacity: 1;
    color: white;

    &:hover {
      color: white !important;
      // background: linear-gradient(to bottom, #d3d3d3, 1%, #9b9b9b, 50%, transparent) !important;
      background: linear-gradient(to bottom,#8f90a6,1%,#2e2e45,50%,transparent) !important
    }
    a {
    color: white;
   }
  }

  .ant-pagination-prev:hover > button.ant-pagination-item-link > span,
  .ant-pagination-next:hover > button.ant-pagination-item-link > span {
    color: rgba(255, 255, 255, 0.85);
  }
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    background-color: transparent;
    color: white;
    border: none;
  }
  .ant-pagination-prev:hover,
  .ant-pagination-next:hover {
    border-radius: var(--border-radius);
    border: 1px solid #f5f5f5;
  }

  .ant-pagination-item-ellipsis {
    color: white !important;
  }
  .ant-pagination-options {
    border: none;
  }
  .ant-pagination-disabled {
    display: none;
  }

  .ant-table-content, .ant-table-body {
    ${theme.myScrollbar}
    overflow: auto !important;
  }
  .ant-table-cell-scrollbar {
    box-shadow: none !important;
  }
  .ant-table-container {
    box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 2px 4px rgba(0, 0, 0, 0.32);
  }
  .ant-table-cell-content {
    font-size: 12px;
  }
  .ant-table-column-title{
    flex: unset !important;
    margin-right: 15px !important;
  }

  .ant-table-column-title,
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    font-size: 12px;
  }

  .ant-table-filter-column, 
  .ant-table-column-sorters {
    justify-content: unset !important;
  }

  .ant-table-selection-extra {
    padding-inline-start: 10px !important;
  }
  
`;

